import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SimpleInput } from "@components/input";
import { SimpleMenu, DateMenu } from "@components/menu";
import { SimpleModal } from "@components/simple-modal";
import { SolidButton } from "@components/button";
import { useBindingContext } from "@/apps/@components/binding-context";
import { LoadingOverlay } from "@/apps/@components/loading-overlay";
import { api, ApiError } from "@utils/index";
import { Salutation } from "@utils/index";
import { throttle } from "lodash";
import dayjs from "dayjs";
import { getCoverImage } from "@utils/api/memberCenterApi";

const salutationOptions = [
	{ value: Salutation.MR, label: "先生" },
	{ value: Salutation.MS, label: "女士" },
	{ value: Salutation.MISS, label: "小姐" },
];

const cities = [
	{ value: "TAIPEI", label: "臺北市" },
	{ value: "NEWTAIPEI", label: "新北市" },
	{ value: "TAOYUAN", label: "桃園市" },
	{ value: "TAICHUNG", label: "臺中市" },
	{ value: "TAINAN", label: "臺南市" },
	{ value: "KAOHSIUNG", label: "高雄市" },
	{ value: "KEELUNG", label: "基隆市" },
	{ value: "HSINCHU", label: "新竹縣" },
	{ value: "HSINCHUC", label: "新竹市" },
	{ value: "MIAOLI", label: "苗栗縣" },
	{ value: "CHANGHUA", label: "彰化縣" },
	{ value: "NANTOU", label: "南投縣" },
	{ value: "YUNLIN", label: "雲林縣" },
	{ value: "CHIAYI", label: "嘉義縣" },
	{ value: "CHIAYIC", label: "嘉義市" },
	{ value: "PINGTUNG", label: "屏東縣" },
	{ value: "YILAN", label: "宜蘭縣" },
	{ value: "HUALIEN", label: "花蓮縣" },
	{ value: "TAITUNG", label: "臺東縣" },
	{ value: "PENGHU", label: "澎湖縣" },
	{ value: "KINMEN", label: "金門縣" },
	{ value: "LIENCHIANG", label: "連江縣" },
];

const initialCheckpoint = {
	salutation: {
		message: "無",
		isValid: true,
	},
	firstName: {
		message: "必須填寫此項",
		isValid: true,
	},
	lastName: {
		message: "必須填寫此項",
		isValid: true,
	},
	phone: {
		message: "必須填寫此項",
		isValid: true,
	},
	otp: {
		message: "必須填寫此項",
		isValid: true,
	},
	email: {
		message: "必須填寫此項",
		isValid: true,
	},
	city: {
		message: "無",
		isValid: true,
	},
	birthday: {
		message: "必須填寫此項",
		isValid: true,
	},
	birthday_year: {
		message: "必須填寫此項",
		isValid: true,
	},
	birthday_month: {
		message: "必須填寫此項",
		isValid: true,
	},
	birthday_day: {
		message: "必須填寫此項",
		isValid: true,
	},
	rule1: {
		message: "請勾選條款",
		isValid: true,
	},
	rule2: {
		message: "請勾選條款",
		isValid: true,
	},
};

// 2026/7/1 00:00 (Asia/Taipei) 前用夏季活動圖，過後改回原圖
// 2026/6/2 00:00 ~ 7/2 23:59:59 (Asia/Taipei) 顯示夏季活動圖，區間外用原圖
const SUMMER_2026_START = new Date("2026-06-02T00:00:00+08:00").getTime();
const SUMMER_2026_END = new Date("2026-07-02T00:00:00+08:00").getTime();
const getCoverMessageSrc = () => {
	const now = Date.now();
	const isSummerCampaign = now >= SUMMER_2026_START && now < SUMMER_2026_END;
	return window.assetUrl(
		isSummerCampaign
			? "/images/liff/binding-page/cover-message-summer-2026.png"
			: "/images/liff/binding-page/cover-message.png",
	);
};

const BindingPage = () => {
	const { from, bindingId } = useBindingContext();
	const navigate = useNavigate();
	const optId = useRef(null);
	const optValidToken = useRef(null);
	// 定義 RWD 樣式
	const [containerClass, setContainerClass] = useState("");
	const [coverClass, setCoverClass] = useState("");
	const [coverMessageClass, setCoverMessageClass] = useState("");
	const [descriptionClass, setDescriptionClass] = useState("");
	const [gridClass, setGridClass] = useState("");
	const [labelClass, setLabelClass] = useState("");
	const [fieldClass, setFieldClass] = useState("");
	const [sendOtpClass, setSendOtpClass] = useState("");
	const [sendedOtpClass, setSendedOtpClass] = useState("");
	const [downImgClass, setDownImgClass] = useState("");
	const [checkboxClass, setCheckboxClass] = useState("");
	const [ruleClass, setRuleClass] = useState("");
	const [submitButtonClass, setSubmitButtonClass] = useState("");
	const [submitContainerClass, setSubmitContainerClass] = useState("");
	const [messageClass, setMessageClass] = useState("");
	const [remarkClass, setRemarkClass] = useState("");
	const [buttonClass, setButtonClass] = useState("");
	const [button2Class, setButton2Class] = useState("");
	const [buttonGroupClass, setButtonGroupClass] = useState("");
	// ---------------------------------------------------------------------------------

	// const navigate = useNavigate();
	const [message, setMessage] = useState("");
	const [isOtpSent, setIsOtpSent] = useState(false);
	const [otpCountDown, setOtpCountDown] = useState(60);
	const [submitModal, setSubmitModal] = useState(false);
	const [messageModal, setMessageModal] = useState(false);
	const [bindingData, setBindingData] = useState({
		salutation: Salutation.MISS,
		firstName: "",
		lastName: "",
		phone: "",
		otp: "",
		email: "",
		city: cities[0].value,
		birthday: {
			year: null,
			month: null,
			day: null,
		},
	});
	const [isBindingLoading, setIsBindingLoading] = useState(false);
	const [coverImageUrl, setCoverImageUrl] = useState(null);
	const [isLoadingCoverImage, setIsLoadingCoverImage] = useState(true);

	const [checkpoint, setCheckPoint] = useState(initialCheckpoint);

	const [confirm, setConfirm] = useState({
		rule1: false,
		rule2: false,
	});

	useLayoutEffect(() => {
		window.addEventListener("resize", handleResize);
		handleResize();

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	// 點擊發送驗證碼後，將 isOtpSent 設為 true，並開始倒數計時，到零秒時恢復原狀
	// 获取封面图片
	useEffect(() => {
		const fetchCoverImage = async () => {
			try {
				const result = await getCoverImage("register");
				if (result.image_url) {
					setCoverImageUrl(result.image_url);
				} else {
					// 如果没有获取到，使用默认图片
					setCoverImageUrl(
						window.assetUrl("/images/liff/binding-page/cover.jpg"),
					);
				}
			} catch (error) {
				console.error("Failed to fetch cover image:", error);
				// 出错时使用默认图片
				setCoverImageUrl(
					window.assetUrl("/images/liff/binding-page/cover.jpg"),
				);
			} finally {
				setIsLoadingCoverImage(false);
			}
		};
		fetchCoverImage();
	}, []);

	useEffect(() => {
		let timer = null;
		if (isOtpSent) {
			timer = setInterval(() => {
				setOtpCountDown((prev) => {
					if (prev === 0) {
						clearInterval(timer);
						setIsOtpSent(false);
						return 60;
					}
					return prev - 1;
				});
			}, 1000);
		}
		return () => clearInterval(timer);
	}, [isOtpSent]);

	const sendOtpHandler = async () => {
		if (!bindingData.phone.match(/^(09[0-9]{8}|9[0-9]{8})$/)) {
			setCheckPoint({
				...checkpoint,
				phone: { message: "請填寫有效的手機號碼", isValid: false },
			});
			return;
		}
		if (bindingData.phone === "") {
			setCheckPoint({
				...checkpoint,
				phone: { message: "必須填寫此項", isValid: false },
			});
			return;
		}

		setIsOtpSent(true);
		setOtpCountDown(60);

		try {
			const params = {
				// 加上+886 如果phone有0開頭則去掉0
				mobile: `+886${bindingData.phone.replace(/^0/, "")}`,
			};
			const res = await api.post("/mobile_validations", params);
			optId.current = res.data.id;
		} catch (error) {
			console.error("Error sending otp", error);
			setIsOtpSent(false); // 發送失敗，停止倒計時
			const errorCode = error.response.data?.error?.code;
			switch (errorCode) {
				// For the same mobile phone number, cannot send verification code again in 15 seconds
				case ApiError.MOBILE_BINDED:
					setMessage("您的手機號碼已經註冊過");
					setSubmitModal(true);
					break;
				case ApiError.VERIFICATION_CODE_IN_COOLDOWN:
					setMessageModal("驗證碼已發送，請稍後再試");
					break;
				default:
					setMessageModal("無法發送驗證碼");
					break;
			}
		}
	};

	const otpValidate = async () => {
		if (optId.current == null) return console.error("尚未發送驗證碼");
		try {
			const params = {
				id: optId.current,
				pin: bindingData.otp,
			};
			const res = await api.post(`/mobile_verify`, params);
			optValidToken.current = res.data.token;
			return true;
		} catch (err) {
			let msg = "無法送出表單";
			if (err.response.data?.error?.code) {
				const errorCode = err.response.data.error.code;
				switch (errorCode) {
					case ApiError.INVALID_FORMAT:
						msg = "資料格式有誤";
						break;
					case ApiError.NOT_FOUND:
						msg = "查無資料";
						break;
					case ApiError.VERIFICATION_CODE_FAILED_TOO_MANY_TIMES:
						msg = "嘗試次數過多";
						break;
					case ApiError.INVALID_VERIFICATION_CODE:
						msg = "錯誤的驗證碼";
						break;
					case ApiError.INVALID_USER_OR_PASSWORD:
						msg = "此手機號碼與帳號不符";
						break;
					default:
						msg = "無法送出表單";
						break;
				}
			}
			console.error(msg);
			return false;
		}
	};

	// 聯絡客服
	const contactHandler = async () => {
		try {
			const params = {
				message:
					"如您在會員註冊過程中遇到任何問題, 請聯絡客服: cs_taiwan@toryburch.com",
			};

			await api.post("/send_oa_message", params);
			window.location.href = process.env.REACT_APP_LINE_OA_URL;
			setSubmitModal(false);
		} catch (error) {
			console.error("Error sending message", error);
		}
	};

	const isValidPhone = (phone) => {
		// 09 開頭，後面接 8 位數字
		const phonePattern = /^09\d{8}$/;
		return phonePattern.test(phone);
	};

	const isValidEmail = (email) => {
		// 電子郵件格式
		const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		return emailPattern.test(email);
	};

	const isValidAge = (birthday) => {
		// 若超過 90 歲或未滿 18 歲，不符合年齡限制
		const now = dayjs();
		const birth = dayjs(`${birthday.year}-${birthday.month}-${birthday.day}`);

		return now.diff(birth, "year") >= 18 && now.diff(birth, "year") < 90;
	};
	function isAtLeast18YearsOld(birthday) {
		const now = dayjs();
		const birth = dayjs(`${birthday.year}-${birthday.month}-${birthday.day}`);

		return now.diff(birth, "year") >= 18;
	}

	const checkBindingData = async () => {
		let currentCheckpoint = { ...initialCheckpoint };
		let hasChange = false;

		console.log("bindingData", bindingData);
		if (bindingData.firstName === "") {
			currentCheckpoint.firstName = { message: "必須填寫此項", isValid: false };
			hasChange = true;
		}

		if (bindingData.lastName === "") {
			currentCheckpoint.lastName = { message: "必須填寫此項", isValid: false };
			hasChange = true;
		}

		if (bindingData.phone === "") {
			currentCheckpoint.phone = { message: "必須填寫此項", isValid: false };
			hasChange = true;
		} else if (isValidPhone(bindingData.phone) === false) {
			currentCheckpoint.phone = {
				message: "請填寫有效的手機號碼",
				isValid: false,
			};
			hasChange = true;
		}

		if (bindingData.otp === "") {
			currentCheckpoint.otp = { message: "必須填寫此項", isValid: false };
			hasChange = true;
		} else {
			const isOtpValid = await otpValidate();
			if (!isOtpValid) {
				currentCheckpoint.otp = {
					message: "請填寫正確的驗證碼",
					isValid: false,
				};
				hasChange = true;
			}
		}

		if (bindingData.email !== "" && isValidEmail(bindingData.email) === false) {
			currentCheckpoint.email = {
				message: "請填寫有效的電郵地址",
				isValid: false,
			};
			hasChange = true;
		}

		if (!bindingData.birthday.year) {
			currentCheckpoint.birthday_year = {
				message: "必須填寫此項",
				isValid: false,
			};
			hasChange = true;
		}
		if (!bindingData.birthday.month) {
			currentCheckpoint.birthday_month = {
				message: "必須填寫此項",
				isValid: false,
			};
			hasChange = true;
		}
		if (!bindingData.birthday.day) {
			currentCheckpoint.birthday_day = {
				message: "必須填寫此項",
				isValid: false,
			};
			hasChange = true;
		}
		if (
			bindingData.birthday.year &&
			bindingData.birthday.month &&
			bindingData.birthday.day &&
			isValidAge(bindingData.birthday) === false
		) {
			let errorMessage = "請填寫有效的出生日期";
			if (!isAtLeast18YearsOld(bindingData.birthday)) {
				errorMessage = "必須年滿18歲";
			}

			currentCheckpoint.birthday = {
				message: errorMessage,
				isValid: false,
			};
			hasChange = true;
		}

		if (confirm.rule1 === false) {
			currentCheckpoint.rule1 = { message: "請勾選條款", isValid: false };
			hasChange = true;
		}

		if (confirm.rule2 === false) {
			currentCheckpoint.rule2 = { message: "請勾選條款", isValid: false };
			hasChange = true;
		}

		if (hasChange) {
			setCheckPoint(currentCheckpoint);
			window.scrollTo({
				top: 0,
				behavior: "smooth",
			});
			return false;
		} else {
			return true;
		}
	};

	const submitHandler = async () => {
		const isDataValid = await checkBindingData();
		if (!isDataValid) {
			return;
		}
		setIsBindingLoading(true);
		try {
			const params = {
				salutation: bindingData.salutation,
				first_name: bindingData.firstName,
				last_name: bindingData.lastName,
				phone: bindingData.phone,
				email: bindingData.email,
				city: bindingData.city,
				birthday: `${bindingData.birthday.year}-${bindingData.birthday.month}-${bindingData.birthday.day}`,
				rule1: confirm.rule1,
				rule2: confirm.rule2,
				from: from,
				binding_id: bindingId,
			};
			const res = await api.post("/binding", params);
			const vipCode = res.data?.vipCode;
			if (vipCode) {
				// 從 splash 2026 來的就直接回活動頁；wallpaper 先進 /thanks 並保留 from
				// （thanks 頁會停留 1.5 秒後自動導往 /wallpaper/home，帶著 from 才能在 reload 後仍讀得到）
				if (from === "splash-2026") {
					navigate("/campaign/splash-2026", { replace: true });
				} else if (from === "wallpaper") {
					navigate("/thanks?from=wallpaper");
				} else {
					navigate("/thanks");
				}
			}
		} catch (error) {
			console.error("Error submitting binding data", error);
			setMessage("無法送出表單");
			setSubmitModal(true);
		} finally {
			setIsBindingLoading(false);
		}
	};

	// resize 時調整樣式
	const handleResize = () => {
		if (window.innerWidth > 640) {
			setContainerClass("px-[30px] flex flex-col items-center");
			setCoverClass("w-full h-auto");
			setCoverMessageClass("w-[231px] h-auto mt-4");
			setGridClass("mt-2 grid grid-cols-12 gap-x-8");
			setLabelClass("text-[21px] tracking-wide");
			setFieldClass("text-[19px]");
			setSendOtpClass("w-full bg-[#d9d9d9] py-2 text-[16px] rounded-[6px]");
			setSendedOtpClass("w-full bg-[#505050] py-2 text-[16px] rounded-[6px]");
			setDescriptionClass("text-[17px] text-[#a8a8a8]");
			setDownImgClass("absolute bottom-3 right-0 h-auto w-4");
			setCheckboxClass("h-6 w-6 rounded-md");
			setRuleClass("ml-5 mt-0.5 space-y-6 text-[16px] -mt-1");
			setSubmitButtonClass("w-[272px] h-auto");
			setSubmitContainerClass("mt-6 mb-16 flex justify-center");
			setButtonClass("px-14 py-1.5 text-[21px] mt-8 rounded-[10px]");
			setButton2Class("px-8 py-1.5 text-[21px] mt-8 rounded-[10px]");
			setButtonGroupClass("grid grid-cols-2 gap-x-8 pb-4 pt-16");
			setMessageClass("text-[25px] mt-24 noto-sans-tc-medium");
			setRemarkClass("text-[21px] mt-3 noto-sans-tc-medium text-[#A6A6A6]");
		} else {
			setContainerClass("px-[5vw] flex flex-col items-center");
			setCoverClass("w-full h-auto");
			setCoverMessageClass("w-[36vw] h-auto mt-4");
			setSubmitButtonClass("w-[42vw] h-auto");
			if (window.innerWidth > 460 && window.innerWidth <= 640) {
				setGridClass("mt-2 grid grid-cols-12 gap-x-6");
				setLabelClass("text-[16px] tracking-wide");
				setFieldClass("text-[15px]");
				setSendOtpClass("w-full bg-[#d9d9d9] py-2 text-[16px] rounded-[5px]");
				setSendedOtpClass("w-full bg-[#505050] py-2 text-[16px] rounded-[5px]");
				setDescriptionClass("text-[15px] text-[#a8a8a8]");
				setDownImgClass("absolute bottom-2.5 right-0 h-auto w-3");
				setCheckboxClass("h-5 w-5 rounded-md");
				setRuleClass("ml-4 space-y-5 text-[15px] -mt-1");
				setSubmitContainerClass("mt-6 mb-16 flex justify-center");
				setButtonGroupClass("grid grid-cols-2 gap-x-8 pb-4 pt-16");
				setButtonClass("px-12 py-1.5 text-[21px] mt-6 rounded-[8px]");
				setButton2Class("px-4 py-1.5 text-[21px] mt-6 rounded-[8px]");
				setMessageClass("text-[23px] mt-20 noto-sans-tc-medium");
				setRemarkClass("text-[18px] mt-3 noto-sans-tc-medium text-[#A6A6A6]");
			} else if (window.innerWidth > 390 && window.innerWidth <= 460) {
				setGridClass("mt-2 grid grid-cols-12 gap-x-4");
				setLabelClass("text-[15px] tracking-wide");
				setFieldClass("text-[13px]");
				setSendOtpClass("w-full bg-[#d9d9d9] py-1.5 text-[12px] rounded-[6px]");
				setSendedOtpClass(
					"w-full bg-[#505050] py-1.5 text-[12px] rounded-[6px]",
				);
				setDescriptionClass("text-[13px] text-[#a8a8a8]");
				setDownImgClass("absolute bottom-2 right-0 h-auto w-2.5");
				setCheckboxClass("h-4 w-4 rounded");
				setRuleClass("ml-3 space-y-4 text-[14px] -mt-1");
				setSubmitContainerClass("mt-6 mb-12 flex justify-center");
				setButtonGroupClass("grid grid-cols-2 gap-x-8 pt-12");
				setButtonClass("px-10 py-2 text-[16px] mt-6 rounded-[4px]");
				setButton2Class("px-2 py-2 text-[16px] mt-6 rounded-[6px]");
				setMessageClass("text-[20px] mt-16 noto-sans-tc-medium");
				setRemarkClass("text-[16px] mt-2 noto-sans-tc-medium text-[#A6A6A6]");
			} else {
				setGridClass("mt-2 grid grid-cols-12 gap-x-3");
				setLabelClass("text-[13px] tracking-wide");
				setFieldClass("text-[12px]");
				setSendOtpClass("w-full bg-[#d9d9d9] py-1.5 text-[12px] rounded-[5px]");
				setSendedOtpClass(
					"w-full bg-[#505050] py-1.5 text-[12px] rounded-[5px]",
				);
				setDescriptionClass("text-[13px] text-[#a8a8a8]");
				setDownImgClass("absolute bottom-2 right-0 h-auto w-2.5");
				setCheckboxClass("h-3 w-3 rounded-sm");
				setRuleClass("ml-2 space-y-4 text-[12px] -mt-1");
				setSubmitContainerClass("mt-6 mb-12 flex justify-center");
				setButtonGroupClass("grid grid-cols-2 gap-x-8 pt-12");
				setButtonClass("px-9 py-1.5 text-[15px] mt-4 rounded-[4px]");
				setButton2Class("px-2 py-1.5 text-[15px] mt-4 rounded-[4px]");
				setMessageClass("text-[18px] mt-16 noto-sans-tc-medium");
				setRemarkClass("text-[14px] mt-2 noto-sans-tc-medium text-[#A6A6A6]");
			}
		}
	};

	// 防止使用者連續點擊觸發多次
	const throttledSendOtpHandler = useMemo(
		() =>
			throttle(
				() => {
					sendOtpHandler();
				},
				1000,
				{ leading: true, trailing: false },
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[bindingData],
	);

	const throttledSubmitHandler = useMemo(
		() =>
			throttle(
				() => {
					submitHandler();
				},
				1000,
				{ leading: true, trailing: false },
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[bindingData, confirm],
	);

	return (
		<div className={containerClass}>
			<LoadingOverlay visible={isBindingLoading} />
			{!isLoadingCoverImage && (
				<img
					alt="cover"
					className={coverClass}
					src={
						coverImageUrl ||
						window.assetUrl("/images/liff/binding-page/cover.jpg")
					}
				/>
			)}
			<img
				alt={"cover-message"}
				className={coverMessageClass}
				src={getCoverMessageSrc()}
			/>
			<div className={"mt-3 flex w-full flex-col"}>
				<p className={descriptionClass}>*必要欄位</p>
				<div className={gridClass}>
					<div className={"col-span-2"}>
						<SimpleMenu
							id={"salutation"}
							label={"稱謂"}
							isRequired={true}
							options={salutationOptions}
							value={bindingData.salutation}
							onChange={(e) =>
								setBindingData({ ...bindingData, salutation: e.target.value })
							}
							labelClass={labelClass}
							fieldClass={fieldClass}
							downImgClass={downImgClass}
							isValid={checkpoint.salutation.isValid}
							errorMessage={checkpoint.salutation.message}
						/>
					</div>
					<div className={"col-span-5"}>
						<SimpleInput
							id={"lastName"}
							label={"姓氏"}
							type={"text"}
							isRequired={true}
							limit={20}
							value={bindingData.lastName}
							onChange={(e) => {
								// 只能輸入中文或英文
								const pattern = /^[\u4e00-\u9fa5a-zA-Z]+$/;
								if (pattern.test(e.target.value) || e.target.value === "") {
									setBindingData({ ...bindingData, lastName: e.target.value });
								}
								setCheckPoint({
									...checkpoint,
									lastName: { message: "必須填寫此項", isValid: true },
								});
							}}
							labelClass={labelClass}
							fieldClass={fieldClass}
							isValid={checkpoint.lastName.isValid}
							errorMessage={checkpoint.lastName.message}
						/>
					</div>
					<div className={"col-span-5"}>
						<SimpleInput
							id={"firstName"}
							label={"名字"}
							type={"text"}
							isRequired={true}
							limit={20}
							value={bindingData.firstName}
							onChange={(e) => {
								// 只能輸入中文或英文
								const pattern = /^[\u4e00-\u9fa5a-zA-Z]+$/;
								if (pattern.test(e.target.value) || e.target.value === "") {
									setBindingData({ ...bindingData, firstName: e.target.value });
								}
								setCheckPoint({
									...checkpoint,
									firstName: { message: "必須填寫此項", isValid: true },
								});
							}}
							labelClass={labelClass}
							fieldClass={fieldClass}
							isValid={checkpoint.firstName.isValid}
							errorMessage={checkpoint.firstName.message}
						/>
					</div>
					<div className={"col-span-4"}>
						<SimpleInput
							id={"phone"}
							label={"電話號碼"}
							type={"tel"}
							isRequired={true}
							value={bindingData.phone}
							onChange={(e) => {
								setBindingData({ ...bindingData, phone: e.target.value });
								setCheckPoint({
									...checkpoint,
									phone: { message: "必須填寫此項", isValid: true },
								});
							}}
							labelClass={labelClass}
							fieldClass={fieldClass}
							isValid={checkpoint.phone.isValid}
							errorMessage={checkpoint.phone.message}
						/>
					</div>
					<div className={"col-span-3 self-center"}>
						{isOtpSent ? (
							<button
								className={sendedOtpClass + " abeezee-regular text-white"}
							>
								{otpCountDown}s
							</button>
						) : (
							<button
								onClick={throttledSendOtpHandler}
								className={sendOtpClass}
							>
								發送驗證碼
							</button>
						)}
					</div>
					<div className={"col-span-5"}>
						<SimpleInput
							id={"otp"}
							label={"請輸入驗證碼"}
							type={"text"}
							isRequired={true}
							limit={6}
							value={bindingData.otp}
							onChange={(e) => {
								setBindingData({ ...bindingData, otp: e.target.value });
								setCheckPoint({
									...checkpoint,
									otp: { message: "必須填寫此項", isValid: true },
								});
							}}
							labelClass={labelClass}
							fieldClass={fieldClass}
							isValid={checkpoint.otp.isValid}
							errorMessage={checkpoint.otp.message}
						/>
					</div>
					<div className={"col-span-6"}>
						<SimpleInput
							id={"email"}
							label={"電郵地址"}
							type={"email"}
							isRequired={false}
							value={bindingData.email}
							onChange={(e) => {
								setBindingData({ ...bindingData, email: e.target.value });
								setCheckPoint({
									...checkpoint,
									email: { message: "必須填寫此項", isValid: true },
								});
							}}
							labelClass={labelClass}
							fieldClass={fieldClass}
							isValid={checkpoint.email.isValid}
							errorMessage={checkpoint.email.message}
						/>
					</div>
					<div className={"col-span-6"}>
						<SimpleMenu
							id={"city"}
							label={"城市"}
							type={"string"}
							isRequired={true}
							options={cities}
							value={bindingData.city}
							onChange={(e) =>
								setBindingData({ ...bindingData, city: e.target.value })
							}
							labelClass={labelClass}
							fieldClass={fieldClass}
							downImgClass={downImgClass}
							isValid={checkpoint.city.isValid}
							errorMessage={checkpoint.city.message}
						/>
					</div>
					<div className={"col-span-7"}>
						<DateMenu
							id={"birthday"}
							label={"生日日期"}
							isRequired={true}
							value={bindingData.birthday}
							onChange={(data) => {
								setBindingData({ ...bindingData, birthday: data });
								setCheckPoint({
									...checkpoint,
									birthday: { message: "必須填寫此項", isValid: true },
									birthday_year: {
										message: "必須填寫此項",
										isValid: data.year
											? true
											: checkpoint.birthday_year.isValid,
									},
									birthday_month: {
										message: "必須填寫此項",
										isValid: data.month
											? true
											: checkpoint.birthday_month.isValid,
									},
									birthday_day: {
										message: "必須填寫此項",
										isValid: data.day ? true : checkpoint.birthday_day.isValid,
									},
								});
							}}
							labelClass={labelClass}
							fieldClass={fieldClass}
							downImgClass={downImgClass}
							isValid={checkpoint.birthday.isValid}
							isValidYear={checkpoint.birthday_year.isValid}
							isValidMonth={checkpoint.birthday_month.isValid}
							isValidDay={checkpoint.birthday_day.isValid}
							errorMessage={checkpoint.birthday.message}
						/>
					</div>
				</div>

				<div className={"mt-10 space-y-10"}>
					<div className="relative flex items-start">
						<div className="flex items-center">
							{confirm.rule1 ? (
								<button
									onClick={() =>
										setConfirm({ ...confirm, rule1: !confirm.rule1 })
									}
									className={
										checkboxClass +
										" border p-[2px] sm:p-[4px]" +
										(checkpoint.rule1.isValid
											? " border-gray-400"
											: " border-red-500")
									}
								>
									<div className={"h-full w-full bg-black"}></div>
								</button>
							) : (
								<button
									onClick={() => {
										setConfirm({ ...confirm, rule1: !confirm.rule1 });
										setCheckPoint({
											...checkpoint,
											rule1: { message: "請勾選條款", isValid: true },
										});
									}}
									className={
										checkboxClass +
										" border p-[2px] sm:p-[4px]" +
										(checkpoint.rule1.isValid
											? " border-gray-400"
											: " border-red-500")
									}
								></button>
							)}
						</div>
						<div className={ruleClass}>
							<p className={"relative"}>
								我聲明我已年滿 18 歲。
								<br />
								<span
									className={
										(checkpoint.rule1.isValid
											? "text-transparent"
											: "text-[#A6A6A6]") + " absolute text-[10px]"
									}
								>
									請勾選條款
								</span>
							</p>
							<p>
								本人同意
								<span className={"kumbh-sans-medium"}> Tory Burch</span>*
								會蒐集、處理利用及傳輸本人的個人資料**
								並嚴格保密，不會洩露、篡改、損毀、出售或非法提供予他人。本人明白並同意「
								<a
									href={"https://www.toryburch.tw/content-privacy"}
									rel="noreferrer"
									className={"underline underline-offset-2"}
								>
									湯麗柏琦隱私政策
								</a>
								」。
							</p>
							<p>
								本人明白並同意{" "}
								<span className={"kumbh-sans-medium"}> Tory Burch </span>
								可能將本人的個人資料分享、揭露或傳送予其附屬或關係企業及
								<span className={"kumbh-sans-medium"}> Tory Burch </span>
								的服務提供者，包括位於美國和本地區以外的其他地區，以作客戶服務、行銷及業務推展促銷之用，並利用、保存截至特定目的消失為止。
							</p>
							<p>
								若本人選擇不提供個人資料，我了解我可能無法接收
								<span className={"kumbh-sans-medium"}> Tory Burch </span>
								最新商品與活動資訊或享有
								<span className={"kumbh-sans-medium"}> Tory Burch </span>
								的完整服務。
							</p>
							<p>
								依據台灣個人資料保護法之規定，我了解就個人資料我擁有下述權利：1.查詢或請求閱覽；2.請求製給複製本；3.請求補充或更正；4.請求停止蒐集、處理或利用；5.請求刪除。如果本人欲行使前述權利，可以透過
								<span className={"kumbh-sans-medium"}>
									{" "}
									cs_taiwan@toryburch.com{" "}
								</span>
								與客戶服務主任聯絡。
							</p>
							<p>
								*「<span className={"kumbh-sans-medium"}> Tory Burch </span>
								」是指香港商湯麗柏琦有限公司台灣分公司。
							</p>
							<p>
								**「個人資料」是指：名字、電郵地址、聯繫號碼、性別、出生日期、年齡、地理位置及其他本人提供的個人資料。
							</p>
						</div>
					</div>
					<div className="relative flex items-start">
						<div className="flex items-center">
							{confirm.rule2 ? (
								<button
									onClick={() =>
										setConfirm({ ...confirm, rule2: !confirm.rule2 })
									}
									className={
										checkboxClass +
										" border p-[2px] sm:p-[4px]" +
										(checkpoint.rule2.isValid
											? " border-gray-400"
											: " border-red-500")
									}
								>
									<div className={"h-full w-full bg-black"}></div>
								</button>
							) : (
								<button
									onClick={() => {
										setConfirm({ ...confirm, rule2: !confirm.rule2 });
										setCheckPoint({
											...checkpoint,
											rule2: { message: "請勾選條款", isValid: true },
										});
									}}
									className={
										checkboxClass +
										" border p-[2px] sm:p-[4px]" +
										(checkpoint.rule2.isValid
											? " border-gray-400"
											: " border-red-500")
									}
								></button>
							)}
						</div>
						<div className={ruleClass}>
							<p>
								本人確認閱讀並同意上述個人資料蒐集之聲明，同意以上述方式處理本人的個人資料，並同意接收由
								<span className={"kumbh-sans-medium"}> Tory Burch </span>、
								其附屬或關連公司及服務提供者以不同形式發出的市場推廣資訊，包括電子郵件、電話、短訊或多媒體短訊，以通知本人關於產品、服務及銷售訊息。請於方框內打鈎以示確認及同意。
							</p>
							<p className={"relative"}>
								如需撤回同意或反對先前所同意的接收直接營銷訊息，您可發送電子郵件至
								<span className={"kumbh-sans-medium"}>
									cs_taiwan@toryburch.com
								</span>
								。<br />
								<span
									className={
										(checkpoint.rule2.isValid
											? "text-transparent"
											: "text-[#A6A6A6]") + " absolute text-[10px]"
									}
								>
									請勾選條款
								</span>
							</p>
						</div>
					</div>
				</div>
				<div className={submitContainerClass}>
					<button onClick={throttledSubmitHandler}>
						<img
							alt={"submit"}
							src={window.assetUrl("/images/liff/binding-page/submit.png")}
							className={submitButtonClass}
						/>
					</button>
				</div>
			</div>
			<SimpleModal
				open={messageModal !== false}
				setOpen={setMessageModal}
				Content={
					<div className={"px-4 py-10 text-center"}>
						<div className={"flex flex-col items-center justify-center"}>
							<p className={messageClass}>{messageModal}</p>
						</div>
						<SolidButton
							onClick={() => setMessageModal(false)}
							label={"確認"}
							buttonClass={button2Class + " w-full mt-[27%]"}
						/>
					</div>
				}
			/>
			<SimpleModal
				open={submitModal}
				setOpen={setSubmitModal}
				Content={
					<div className={"px-4 py-10 text-center"}>
						<div className={"flex flex-col items-center justify-center"}>
							<p className={messageClass}>{message}</p>
							<p className={remarkClass}>注：若有問題請聯絡客服</p>
						</div>
						<div className={buttonGroupClass}>
							<SolidButton
								onClick={() => contactHandler()}
								label={"聯絡客服"}
								buttonClass={button2Class + " w-full"}
							/>
							<SolidButton
								onClick={() => setSubmitModal(false)}
								label={"返回"}
								buttonClass={buttonClass + " w-full"}
							/>
						</div>
					</div>
				}
			/>
		</div>
	);
};

export default BindingPage;
